import { useEffect, useRef, useState } from 'react';
import '../Dashboard.scss';
import * as echarts from 'echarts';
import IconButton from '@mui/material/IconButton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import Button from '@mui/material/Button';
import { COMPACT_FONT_SIZE, downloadCSV, downloadIcon } from './utils';

type InputData = {
  total_emissions: Record<string, number>;
  leaking_equipment_count: Record<string, number>;
  equipment_count: Record<string, number>;
  population_ef: Record<string, number>;
  leaker_ef: Record<string, number | null>;
  equipment_type: Record<string, string>;
};

type OutputData = {
  'Original Equipment Type': string;
  TotalEmissionRate: number;
  PercentageOfTotal: number;
  EquipmentCount: number;
}[];

const processPopulationData = (data: void | any[], type: string) => {
  if (!data) return [];

  const order =
    type === 'equip'
      ? [
          'Well',
          'Separator',
          'Tank',
          'Compressor',
          'Flare / Combustor',
          'Dehydrator',
          'Facility Piping',
        ]
      : [
          'Wellpad',
          'Compressor Station',
          'Gathering and Boosting Unit',
          'Gas Processing Plant',
          'Dehydration Facility',
        ];

  const updated = data
    .map((item: any) => {
      let itemType = type === 'equip' ? item[0] : item['Facility Type'];
      if (itemType === 'Flare') {
        if (type === 'equip') {
          item[0] = 'Flare / Combustor';
        } else {
          item['Facility Type'] = 'Flare / Combustor';
        }
      }

      return item;
    })
    .sort((a, b) => {
      const orderA = order.indexOf(
        type === 'equip' ? a[0] : a['Facility Type'],
      );
      const orderB = order.indexOf(
        type === 'equip' ? b[0] : b['Facility Type'],
      );

      if (orderA === -1) {
        return 1;
      }
      if (orderA > orderB) {
        return 1;
      }
      if (orderA < orderB) {
        return -1;
      }
      if (orderB === -1) {
        return 1;
      }
      return -1;
    });

  return updated;
};

function transformData(input: InputData): OutputData {
  const totalEmissionRates: Record<string, number> = {};

  for (const equipmentType in input.total_emissions) {
    const leakingCount = input.leaking_equipment_count[equipmentType];
    const populationEF = input.population_ef[equipmentType];
    const leakerEF = input.leaker_ef[equipmentType] || 0;

    const emissionRate =
      leakingCount * leakerEF +
      (input.equipment_count[equipmentType] - leakingCount) * populationEF;

    totalEmissionRates[equipmentType] = emissionRate;
  }

  const grandTotalEmissionRate = Object.values(totalEmissionRates).reduce(
    (sum, rate) => sum + rate,
    0,
  );

  const output: OutputData = Object.keys(totalEmissionRates)
    .map((equipmentType) => {
      const totalEmissionRate = totalEmissionRates[equipmentType];
      const percentageOfTotal =
        (totalEmissionRate / grandTotalEmissionRate) * 100;
      const equipmentCount =
        input.leaking_equipment_count[equipmentType] +
        input.equipment_count[equipmentType];

      return {
        'Original Equipment Type': input.equipment_type[equipmentType],
        TotalEmissionRate: totalEmissionRate,
        PercentageOfTotal: percentageOfTotal,
        EquipmentCount: equipmentCount,
      };
    })
    .filter((item) => item.TotalEmissionRate > 0)
    .sort((a, b) => b.TotalEmissionRate - a.TotalEmissionRate);

  return output;
}

type InputFacilityData = {
  facility_type: Record<string, string>;
  emission_rate: Record<string, number>;
  population_facility: Record<string, number>;
  leaker_facility: Record<string, number | null>;
  count: Record<string, number>;
  emitted_count: Record<string, number>;
};

type OutputFacilityData = {
  'Facility Type': string;
  TotalEmissionRate: number;
  PercentageOfTotal: number;
  EquipmentCount: number;
}[];

function clearResults<T extends { [key: string]: Record<string, any> }>(
  input: T,
  type: string,
): T {
  const listToRemove =
    type === 'equip'
      ? ['VRU', 'Generator', 'Other', 'Facility Piping']
      : [
          'Coal Mine',
          'Coal Vent',
          'Concentrated Animal Feeding Operation',
          'Conv_Well',
          'Heater',
          'Interconnect',
          'Landfill',
          'Tank Batteries',
          'Meter Site',
          'Underground Gas Storage',
        ];

  let listToRemoveIndexes: number[] = [];

  Object.entries(input).forEach(([key, value]) => {
    if (key === 'facility_type' || key === 'equipment_type') {
      Object.values(value).forEach((item, index) => {
        if (listToRemove.includes(item)) {
          listToRemoveIndexes.push(index);
        }
      });
    }
  });

  const filteredResults = Object.entries(input).map(([key, value]) => {
    const filteredItem = Object.entries(value).map(
      ([subKey, subValue], index) => {
        if (listToRemoveIndexes.includes(index)) {
          return null;
        } else {
          return [subKey, subValue];
        }
      },
    );

    const filteredArray = filteredItem.filter(
      (subItem): subItem is [string, any] => subItem !== null,
    );

    return [key, Object.fromEntries(filteredArray)];
  });

  return Object.fromEntries(filteredResults) as T;
}

function transformFacilityData(input: InputFacilityData): OutputFacilityData {
  const { facility_type, emission_rate, count } = input;

  const totalEmissionRate = Object.values(emission_rate).reduce(
    (sum, rate) => sum + rate,
    0,
  );

  const result: OutputFacilityData = Object.keys(facility_type).map((key) => {
    const facilityType = facility_type[key];
    const totalEmissionRateForFacility = emission_rate[key] || 0;
    const equipmentCount = count[key] || 0;

    return {
      'Facility Type': facilityType,
      TotalEmissionRate: totalEmissionRateForFacility,
      PercentageOfTotal:
        (totalEmissionRateForFacility / totalEmissionRate) * 100,
      EquipmentCount: equipmentCount,
    };
  });

  return result.filter((item) => item.TotalEmissionRate > 0);
}

const ChartComponent = ({
  results,
  dataType,
  compact = false,
  ci,
}: {
  results: any;
  dataType: string;
  compact?: boolean;
  ci?: boolean;
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chartType, setChartType] = useState('pie');

  useEffect(() => {
    if (!chartRef.current) return;

    chartInstance.current = echarts.init(chartRef.current);

    return () => {
      chartInstance.current?.dispose();
    };
  }, []);

  const updateChartOptions = () => {
    if (!chartInstance.current || results.length === 0) return;

    const clearedResults = clearResults(results, dataType);

    const updatedResults =
      dataType === 'equip'
        ? transformData(clearedResults)
        : transformFacilityData(clearedResults);

    const processed_data = processPopulationData(updatedResults, dataType);

    const pieData = processed_data.map((e: { [x: string]: any }) => ({
      value: e['PercentageOfTotal'],
      name: `${e['Original Equipment Type'] || e['Updated Equipment Type'] || e['Facility Type']} (${e['EquipmentCount']})`,
    }));

    const barData = processed_data.map((e: { [x: string]: any }) => ({
      name:
        e['Original Equipment Type'] ||
        e['Updated Equipment Type'] ||
        e['Facility Type'],
      value: e['TotalEmissionRate'],
    }));

    const optionPie = {
      title: {
        text: 'Total Emissions',
        left: 'center',
        ...(compact
          ? {
              textStyle: {
                fontSize: COMPACT_FONT_SIZE.toString() + 'px',
              },
            }
          : {}),
      },
      grid: {
        top: compact ? 30 : 60,
        bottom: compact ? 5 : 40,
        left: compact ? 50 : 50,
        right: compact ? 20 : 30,
      },
      tooltip: {
        trigger: 'item',
        formatter: function (params: {
          seriesName: any;
          name: any;
          value: any;
          percent: any;
        }) {
          const { seriesName, name, value } = Array.isArray(params)
            ? params[0]
            : params;
          return `${seriesName} <br/>${name}: ${Number(value || '0').toFixed(0)}%`;
        },
      },
      legend: {
        show: !compact,
        orient: 'vertical',
        left: '10px',
        top: 'middle',
        data: pieData.map((item: { name: any }) => item.name),
      },
      series: [
        {
          name: 'Emission Rates',
          type: 'pie',
          radius: '50%',
          data: pieData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      ],
      toolbox: {
        show: !compact,
        right: '1%',
        feature: {
          saveAsImage: {},
          myDownload: {
            show: true,
            title: 'Download data',
            icon: downloadIcon,
            onclick: function () {
              downloadCSV(pieData);
            },
          },
          dataZoom: [
            {
              type: 'slider',
            },
            {
              type: 'inside',
            },
          ],
        },
      },
      animation: false,
    };

    const optionBar = {
      title: {
        text: 'Total Emissions (kg/h)',
        left: 'center',
        ...(compact
          ? {
              textStyle: {
                fontSize: COMPACT_FONT_SIZE.toString() + 'px',
              },
            }
          : {}),
      },
      grid: {
        top: compact ? 30 : 60,
        bottom: compact ? 20 : 40,
        left: compact ? 65 : 50,
        right: compact ? 20 : 30,
      },
      xAxis: {
        type: 'category',
        data: barData.map((item: { name: any }) => item.name),
        name: 'Equipment Type',
        nameLocation: 'middle',
        nameGap: 25,
        axisLabel: {
          interval: 0,
          rotate: 0,
          formatter: function (value: string) {
            return value;
          },
        },
      },
      yAxis: {
        type: 'value',
        name: 'Total Emissions (kg/h)',
        nameRotate: 90,
        position: 'left',
        offset: 0,
        nameLocation: 'left',
        nameGap: 50,
      },
      series: [
        {
          data: barData.map((item: { value: any }) =>
            (item.value || 0).toFixed(1),
          ),
          type: 'bar',
        },
      ],
      tooltip: {
        trigger: 'axis',
        formatter: function (params: {
          seriesName: any;
          name: any;
          value: any;
          percent: any;
        }) {
          const { name, value } = Array.isArray(params) ? params[0] : params;
          return `${name}: ${Number(value || '0').toFixed(0)}`;
        },
      },
      toolbox: {
        show: isFullscreen,
        right: '2%',
        feature: {
          saveAsImage: {},
          myDownload: {
            show: true,
            title: 'Download data',
            icon: downloadIcon,
            onclick: function () {
              downloadCSV(barData);
            },
          },
          dataZoom: [
            {
              type: 'slider',
            },
            {
              type: 'inside',
            },
          ],
        },
      },
      animation: false,
    };

    chartInstance.current.setOption(
      chartType === 'pie' ? optionPie : optionBar,
    );
  };

  useEffect(() => {
    updateChartOptions();

    if (chartInstance.current) {
      chartInstance.current.resize();
    }
  }, [results, isFullscreen, chartType, compact]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const toggleChartType = () => {
    setChartType(chartType === 'pie' ? 'bar' : 'pie');
  };

  return (
    <div className={`chart-container ${isFullscreen ? 'fullscreen' : ''}`}>
      <IconButton
        onClick={toggleFullscreen}
        style={{
          position: 'absolute',
          top: '0px',
          left: '10px',
          zIndex: 1000,
        }}
      >
        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </IconButton>
      <Button
        onClick={toggleChartType}
        style={{
          position: 'absolute',
          top: '0px',
          right: compact ? '0' : '160px',
          zIndex: 1000,
        }}
      >
        {chartType === 'pie' ? 'Switch to Bar Chart' : 'Switch to Pie Chart'}
      </Button>
      <div ref={chartRef} className={'chart pie'} />
    </div>
  );
};

export default ChartComponent;
